
import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout/Layout';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Tag, ExternalLink, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface BlogPost {
  slug: string;
  title: string;
  updated_at: string;
  published_date: string;
  categories: string[] | null;
  tags: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
}

interface ServiceLocation {
  name: string;
}

const SitemapPage = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [locations, setLocations] = useState<ServiceLocation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSitemapData();
  }, []);

  const fetchSitemapData = async () => {
    try {
      // Fetch published blog posts with SEO data
      const { data: postsData } = await supabase
        .from('blog_posts')
        .select('slug, title, updated_at, published_date, categories, tags, seo_title, seo_description')
        .eq('status', 'published')
        .order('published_date', { ascending: false });

      // Fetch service locations
      const { data: locationsData } = await supabase
        .from('service_locations')
        .select('name')
        .order('name');

      setBlogPosts(postsData || []);
      setLocations(locationsData || []);
    } catch (error) {
      console.error('Error fetching sitemap data:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractCityFromPost = (post: BlogPost): string | null => {
    const title = post.title.toLowerCase();
    
    // Enhanced location extraction patterns
    const locationPatterns = [
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:Roofing|Church|Roof|Storm|Damage|Repair|Replacement)/i,
      /\bin\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*(?:NC|North Carolina)/i,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*(?:NC|North Carolina)\s+(?:Roofing|Church|Roof)/i,
      /Best\s+Roofer(?:s)?\s+in\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+Area\s+Roofing/i,
    ];

    for (const pattern of locationPatterns) {
      const match = post.title.match(pattern);
      if (match && match[1]) {
        let location = match[1].trim();
        const excludeWords = ['Your', 'The', 'Why', 'When', 'Faith', 'Meets', 'Engineering', 'Homeowners', 'Are', 'Switching', 'Top', 'Best'];
        if (!excludeWords.some(word => location.includes(word))) {
          return location;
        }
      }
    }

    // Check tags for location info
    if (post.tags) {
      for (const tag of post.tags) {
        if (tag.includes('-roofing') && !tag.includes('roofing-')) {
          const location = tag.replace('-roofing', '').replace(/-/g, ' ');
          return location.charAt(0).toUpperCase() + location.slice(1);
        }
      }
    }

    return null;
  };

  // Primary Navigation Pages (from navbar)
  const primaryNavSection = {
    title: 'Primary Navigation',
    description: 'Main website pages for core business functions',
    priority: 'high',
    links: [
      { name: 'Home', path: '/', description: 'Welcome page and company overview', lastmod: new Date().toISOString() },
      { name: 'Home (Alternative)', path: '/home', description: 'Alternative home page route', lastmod: new Date().toISOString() },
      { name: 'Services', path: '/services', description: 'Complete roofing services offered', lastmod: new Date().toISOString() },
      { name: 'Locations', path: '/locations', description: 'Service areas and coverage map', lastmod: new Date().toISOString() },
      { name: 'Brands', path: '/brands', description: 'Roofing brands and materials we work with', lastmod: new Date().toISOString() },
      { name: 'Resources', path: '/resources', description: 'Helpful guides and information', lastmod: new Date().toISOString() },
      { name: 'About Us', path: '/about', description: 'Company history and team information', lastmod: new Date().toISOString() },
      { name: 'Contact Us', path: '/contact', description: 'Get in touch for estimates', lastmod: new Date().toISOString() },
      { name: 'Blog', path: '/blog', description: 'Roofing tips, news, and insights', lastmod: new Date().toISOString() },
    ],
  };

  // Service Pages (from navbar dropdowns)
  const serviceSection = {
    title: 'Roofing Services',
    description: 'Comprehensive roofing solutions and specialized services',
    priority: 'high',
    links: [
      { name: 'Residential Roofing', path: '/residential-roofing', description: 'Home roofing services and solutions' },
      { name: 'Commercial Roofing', path: '/commercial-roofing', description: 'Business and commercial roofing solutions' },
      { name: 'Church Steeple Repair', path: '/specialty-services/church-steeple-repair', description: 'Expert church steeple restoration and repair' },
      { name: 'Roof Coatings', path: '/specialty-services/roof-coating', description: 'Protective roof coating applications' },
      { name: 'Storm Damage', path: '/storm-damage', description: 'Emergency storm damage restoration' },
      { name: 'Professional Church Steeple Repair Raleigh', path: '/professional-church-steeple-repair-and-replacement-services-in-raleigh-nc-and-surrounding-areas', description: 'Church steeple services in Raleigh, NC area' },
    ],
  };

  // Tools & Actions (from navbar)
  const toolsSection = {
    title: 'Tools & Actions',
    description: 'Interactive tools and quick actions for customers',
    priority: 'high',
    links: [
      { name: 'INSTANT Estimate', path: 'https://carolinatrophyroofs.sky-quote.com/Instant-Estimate', description: 'Get an instant roofing estimate online', external: true },
      { name: 'Book Inspection', path: '/book-inspection', description: 'Schedule your free roofing inspection' },
      { name: 'Design Your Roof', path: '/design-your-roof', description: 'Interactive roof design tool' },
      { name: 'Apply For Financing', path: '/financing', description: 'Roofing project financing options' },
      { name: 'Careers', path: '/careers', description: 'Career opportunities at Carolina Trophy Roofs' },
    ],
  };

  // Information & Resources (from navbar and footer)
  const informationSection = {
    title: 'Information & Resources',
    description: 'Educational content and helpful information',
    priority: 'medium',
    links: [
      { name: 'Gallery', path: '/gallery', description: 'Project photos and work showcase' },
      { name: 'Photo Gallery', path: '/gallery/', description: 'Alternative gallery route' },
      { name: 'Resources', path: '/resources', description: 'Helpful guides and roofing information' },
      { name: 'FAQ', path: '/frequently-asked-questions', description: 'Frequently asked questions about roofing' },
      { name: 'Roof Life Expectancy Report', path: '/roof-life-expectancy-report', description: 'Comprehensive roof lifespan analysis' },
      { name: 'Roof Installations & Materials', path: '/roof-installations-and-materials', description: 'Complete guide to roofing materials and installation' },
      { name: 'Service Areas', path: '/service-areas', description: 'Areas we serve in Eastern North Carolina' },
      { name: 'Sitemap', path: '/sitemap', description: 'Complete site navigation and page index' },
    ],
  };

  // Detailed Resource Pages (from footer)
  const resourcesSection = {
    title: 'Resource Center',
    description: 'Educational content and helpful information',
    priority: 'medium',
    links: [
      { name: 'General Roofing FAQ', path: '/resources/general-roofing-faq', description: 'Frequently asked roofing questions' },
      { name: 'Roofing Materials FAQ', path: '/resources/roofing-materials-faq', description: 'Material-specific questions and answers' },
      { name: 'Roofing Costs FAQ', path: '/resources/roofing-costs-faq', description: 'Cost and pricing related questions' },
      { name: 'Understanding Roofing Materials', path: '/resources/understanding-roofing-materials', description: 'Guide to different roofing material options' },
      { name: 'Roof Replacement Process', path: '/resources/roof-replacement-process', description: 'Step-by-step roof replacement guide' },
      { name: 'How to Identify Roof Damage', path: '/resources/identify-roof-damage', description: 'Signs and symptoms of roof damage' },
      { name: 'Insurance Claim Process', path: '/resources/insurance-claim-process', description: 'Navigate the insurance claim process' },
      { name: 'Working with Insurance Adjusters', path: '/resources/working-with-adjusters', description: 'Tips for working with insurance adjusters' },
      { name: 'Documentation Requirements', path: '/resources/documentation-requirements', description: 'Required documentation for insurance claims' },
      { name: 'Manufacturer Warranties', path: '/resources/manufacturer-warranties', description: 'Understanding manufacturer warranty coverage' },
      { name: 'Workmanship Warranty Information', path: '/resources/workmanship-warranty', description: 'Details about our workmanship warranties' },
      { name: 'Warranty Registration', path: '/resources/warranty-registration', description: 'How to register your roofing warranty' },
      { name: 'Roof Ventilation Guide', path: '/resources/roof-ventilation-guide', description: 'Complete guide to roof ventilation systems' },
    ],
  };

  // Special Pages
  const specialPagesSection = {
    title: 'Special Pages',
    description: 'Unique landing pages and specialized content',
    priority: 'medium',
    links: [
      { name: 'Best Roofer Near Me Greenville NC', path: '/best-roofer-near-me-in-greenville-nc-carolina-trophy-roofs', description: 'Top roofing contractor in Greenville, North Carolina' },
    ],
  };

  // All actual location pages from the codebase - manually verified
  const actualLocationPages = [
    { name: 'Afton', path: '/locations/afton' },
    { name: 'Ahoskie', path: '/locations/ahoskie' },
    { name: 'Airlie', path: '/locations/airlie' },
    { name: 'Alert', path: '/locations/alert' },
    { name: 'Angier', path: '/locations/angier' },
    { name: 'Apex', path: '/locations/apex' },
    { name: 'Archer Lodge', path: '/locations/archer-lodge' },
    { name: 'Asheville', path: '/locations/asheville' },
    { name: 'Aulander', path: '/locations/aulander' },
    { name: 'Aurora', path: '/locations/aurora' },
    { name: 'Autryville', path: '/locations/autryville' },
    { name: 'Avon', path: '/locations/avon' },
    { name: 'Ayden', path: '/locations/ayden' },
    { name: 'Bailey', path: '/locations/bailey' },
    { name: 'Bath', path: '/locations/bath' },
    { name: 'Battleboro', path: '/locations/battleboro' },
    { name: 'Belhaven', path: '/locations/belhaven' },
    { name: 'Bellarthur', path: '/locations/bellarthur' },
    { name: 'Benson', path: '/locations/benson' },
    { name: 'Bethel', path: '/locations/bethel' },
    { name: 'Beulaville', path: '/locations/beulaville' },
    { name: 'Black Creek', path: '/locations/black-creek' },
    { name: 'Broadway', path: '/locations/broadway' },
    { name: 'Bunn', path: '/locations/bunn' },
    { name: 'Burgaw', path: '/locations/burgaw' },
    { name: 'Buxton', path: '/locations/buxton' },
    { name: 'Calypso', path: '/locations/calypso' },
    { name: 'Cary', path: '/locations/cary' },
    { name: 'Castalia', path: '/locations/castalia' },
    { name: 'Centerville', path: '/locations/centerville' },
    { name: 'Chapel Hill', path: '/locations/chapel-hill' },
    { name: 'Chinquapin', path: '/locations/chinquapin' },
    { name: 'Chocowinity', path: '/locations/chocowinity' },
    { name: 'Clayton', path: '/locations/clayton' },
    { name: 'Clinton', path: '/locations/clinton' },
    { name: 'Coats', path: '/locations/coats' },
    { name: 'Colerain', path: '/locations/colerain' },
    { name: 'Columbia', path: '/locations/columbia' },
    { name: 'Conetoe', path: '/locations/conetoe' },
    { name: 'Conway', path: '/locations/conway' },
    { name: 'Cove City', path: '/locations/cove-city' },
    { name: 'Creedmoor', path: '/locations/creedmoor' },
    { name: 'Creswell', path: '/locations/creswell' },
    { name: 'Deep Run', path: '/locations/deep-run' },
    { name: 'Dortches', path: '/locations/dortches' },
    { name: 'Dover', path: '/locations/dover' },
    { name: 'Dudley', path: '/locations/dudley' },
    { name: 'Dunn', path: '/locations/dunn' },
    { name: 'Durham', path: '/locations/durham' },
    { name: 'Edenton', path: '/locations/edenton' },
    { name: 'Elizabeth City', path: '/locations/elizabeth-city' },
    { name: 'Elizabethtown', path: '/locations/elizabethtown' },
    { name: 'Elm City', path: '/locations/elm-city' },
    { name: 'Enfield', path: '/locations/enfield' },
    { name: 'Engelhard', path: '/locations/engelhard' },
    { name: 'Erwin', path: '/locations/erwin' },
    { name: 'Eureka', path: '/locations/eureka' },
    { name: 'Faison', path: '/locations/faison' },
    { name: 'Falkland', path: '/locations/falkland' },
    { name: 'Farmville', path: '/locations/farmville' },
    { name: 'Fayetteville', path: '/locations/fayetteville' },
    { name: 'Fountain', path: '/locations/fountain' },
    { name: 'Four Oaks', path: '/locations/four-oaks' },
    { name: 'Franklinton', path: '/locations/franklinton' },
    { name: 'Fremont', path: '/locations/fremont' },
    { name: 'Fuquay-Varina', path: '/locations/fuquay-varina' },
    { name: 'Garland', path: '/locations/garland' },
    { name: 'Garner', path: '/locations/garner' },
    { name: 'Garysburg', path: '/locations/garysburg' },
    { name: 'Gaston', path: '/locations/gaston' },
    { name: 'Gatesville', path: '/locations/gatesville' },
    { name: 'Godwin', path: '/locations/godwin' },
    { name: 'Gold Point', path: '/locations/gold-point' },
    { name: 'Goldsboro', path: '/locations/goldsboro' },
    { name: 'Greenville', path: '/locations/greenville' },
    { name: 'Greenville NC', path: '/locations/greenville-nc' },
    { name: 'Grifton', path: '/locations/grifton' },
    { name: 'Grimesland', path: '/locations/grimesland' },
    { name: 'Halifax', path: '/locations/halifax' },
    { name: 'Hamilton', path: '/locations/hamilton' },
    { name: 'Harrells', path: '/locations/harrells' },
    { name: 'Hassell', path: '/locations/hassell' },
    { name: 'Hatteras', path: '/locations/hatteras' },
    { name: 'Havelock', path: '/locations/havelock' },
    { name: 'Henderson', path: '/locations/henderson' },
    { name: 'Hertford', path: '/locations/hertford' },
    { name: 'Hickory', path: '/locations/hickory' },
    { name: 'Hobgood', path: '/locations/hobgood' },
    { name: 'Hollister', path: '/locations/hollister' },
    { name: 'Holly Springs', path: '/locations/holly-springs' },
    { name: 'Hookerton', path: '/locations/hookerton' },
    { name: 'Jackson', path: '/locations/jackson' },
    { name: 'Jacksonville', path: '/locations/jacksonville' },
    { name: 'Jamesville', path: '/locations/jamesville' },
    { name: 'Kelford', path: '/locations/kelford' },
    { name: 'Kenansville', path: '/locations/kenansville' },
    { name: 'Kenly', path: '/locations/kenly' },
    { name: 'Kill Devil Hills', path: '/locations/kill-devil-hills' },
    { name: 'Kinston', path: '/locations/kinston' },
    { name: 'Kitty Hawk', path: '/locations/kitty-hawk' },
    { name: 'Knightdale', path: '/locations/knightdale' },
    { name: 'La Grange', path: '/locations/la-grange' },
    { name: 'Leggett', path: '/locations/leggett' },
    { name: 'Lewiston Woodville', path: '/locations/lewiston' },
    { name: 'Lillington', path: '/locations/lillington' },
    { name: 'Littleton', path: '/locations/littleton' },
    { name: 'Louisburg', path: '/locations/louisburg' },
    { name: 'Lucama', path: '/locations/lucama' },
    { name: 'Lumberton', path: '/locations/lumberton' },
    { name: 'Macclesfield', path: '/locations/macclesfield' },
    { name: 'Macon', path: '/locations/macon' },
    { name: 'Magnolia', path: '/locations/magnolia' },
    { name: 'Manteo', path: '/locations/manteo' },
    { name: 'Maysville', path: '/locations/maysville' },
    { name: 'Micro', path: '/locations/micro' },
    { name: 'Middlesex', path: '/locations/middlesex' },
    { name: 'Momeyer', path: '/locations/momeyer' },
    { name: 'Morehead City', path: '/locations/morehead-city' },
    { name: 'Morrisville', path: '/locations/morrisville' },
    { name: 'Mount Olive', path: '/locations/mount-olive' },
    { name: 'Murfreesboro', path: '/locations/murfreesboro' },
    { name: 'Nags Head', path: '/locations/nags-head' },
    { name: 'Nashville', path: '/locations/nashville' },
    { name: 'New Bern', path: '/locations/new-bern' },
    { name: 'Newton Grove', path: '/locations/newton-grove' },
    { name: 'Norlina', path: '/locations/norlina' },
    { name: 'Oak City', path: '/locations/oak-city' },
    { name: 'Ocracoke', path: '/locations/ocracoke' },
    { name: 'Oxford', path: '/locations/oxford' },
    { name: 'Pantego', path: '/locations/pantego' },
    { name: 'Pikeville', path: '/locations/pikeville' },
    { name: 'Pine Level', path: '/locations/pine-level' },
    { name: 'Pinetops', path: '/locations/pinetops' },
    { name: 'Pink Hill', path: '/locations/pink-hill' },
    { name: 'Pittsboro', path: '/locations/pittsboro' },
    { name: 'Plymouth', path: '/locations/plymouth' },
    { name: 'Pollocksville', path: '/locations/pollocksville' },
    { name: 'Powellsville', path: '/locations/powellsville' },
    { name: 'Princeton', path: '/locations/princeton' },
    { name: 'Raleigh', path: '/locations/raleigh' },
    { name: 'Red Oak', path: '/locations/red-oak' },
    { name: 'Rich Square', path: '/locations/rich-square' },
    { name: 'Richlands', path: '/locations/richlands' },
    { name: 'Roanoke Rapids', path: '/locations/roanoke-rapids' },
    { name: 'Robersonville', path: '/locations/robersonville' },
    { name: 'Rocky Mount', path: '/locations/rocky-mount' },
    { name: 'Rocky Mount NC', path: '/locations/rockymount-nc' },
    { name: 'Rodanthe', path: '/locations/rodanthe' },
    { name: 'Rolesville', path: '/locations/rolesville' },
    { name: 'Roper', path: '/locations/roper' },
    { name: 'Rose Hill', path: '/locations/rose-hill' },
    { name: 'Roseboro', path: '/locations/roseboro' },
    { name: 'Salemburg', path: '/locations/salemburg' },
    { name: 'Salvo', path: '/locations/salvo' },
    { name: 'Sanford', path: '/locations/sanford' },
    { name: 'Saratoga', path: '/locations/saratoga' },
    { name: 'Scotland Neck', path: '/locations/scotland-neck' },
    { name: 'Seaboard', path: '/locations/seaboard' },
    { name: 'Selma', path: '/locations/selma' },
    { name: 'Seven Springs', path: '/locations/seven-springs' },
    { name: 'Sharpsburg', path: '/locations/sharpsburg' },
    { name: 'Sims', path: '/locations/sims' },
    { name: 'Smithfield', path: '/locations/smithfield' },
    { name: 'Snow Hill', path: '/locations/snow-hill' },
    { name: 'Speed', path: '/locations/speed' },
    { name: 'Spring Hope', path: '/locations/spring-hope' },
    { name: 'Stantonsburg', path: '/locations/stantonsburg' },
    { name: 'Stokes', path: '/locations/stokes' },
    { name: 'Swan Quarter', path: '/locations/swan-quarter' },
    { name: 'Swansboro', path: '/locations/swansboro' },
    { name: 'Tarboro', path: '/locations/tarboro' },
    { name: 'Teachey', path: '/locations/teachey' },
    { name: 'Thelma', path: '/locations/thelma' },
    { name: 'Trenton', path: '/locations/trenton' },
    { name: 'Turkey', path: '/locations/turkey' },
    { name: 'Tyner', path: '/locations/tyner' },
    { name: 'Vanceboro', path: '/locations/vanceboro' },
    { name: 'Vaughan', path: '/locations/vaughan' },
    { name: 'Wade', path: '/locations/wade' },
    { name: 'Wake Forest', path: '/locations/wake-forest' },
    { name: 'Wallace', path: '/locations/wallace' },
    { name: 'Walstonburg', path: '/locations/walstonburg' },
    { name: 'Wanchese', path: '/locations/wanchese' },
    { name: 'Warden', path: '/locations/warden' },
    { name: 'Warrenton', path: '/locations/warrenton' },
    { name: 'Warsaw', path: '/locations/warsaw' },
    { name: 'Washington', path: '/locations/washington' },
    { name: 'Waves', path: '/locations/waves' },
    { name: 'Waynesville', path: '/locations/waynesville' },
    { name: 'Weldon', path: '/locations/weldon' },
    { name: 'Wendell', path: '/locations/wendell' },
    { name: 'Whitakers', path: '/locations/whitakers' },
    { name: 'Whiteville', path: '/locations/whiteville' },
    { name: 'Williamston', path: '/locations/williamston' },
    { name: 'Wilmington', path: '/locations/wilmington' },
    { name: 'Wilson', path: '/locations/wilson' },
    { name: 'Windsor', path: '/locations/windsor' },
    { name: 'Winfall', path: '/locations/winfall' },
    { name: 'Winterville', path: '/locations/winterville' },
    { name: 'Winton', path: '/locations/winton' },
    { name: 'Woodland', path: '/locations/woodland' },
    { name: 'Yanceyville', path: '/locations/yanceyville' },
    { name: 'Youngsville', path: '/locations/youngsville' },
    { name: 'Zebulon', path: '/locations/zebulon' },
  ];

  const locationSection = {
    title: 'All Service Locations',
    description: `Complete coverage across Eastern North Carolina (${actualLocationPages.length} locations)`,
    priority: 'high',
    links: actualLocationPages.map(location => ({
      name: `${location.name}, NC Roofing Services`,
      path: location.path,
      description: `Professional roofing contractors serving ${location.name}, North Carolina and surrounding areas`,
      lastmod: new Date().toISOString()
    })),
  };

  // Legal and compliance pages (from footer)
  const legalSection = {
    title: 'Legal & Compliance',
    description: 'Terms, policies, and legal information',
    priority: 'low',
    links: [
      { name: 'Privacy Policy', path: '/privacy', description: 'How we protect and handle your personal information' },
      { name: 'SMS Terms & Conditions', path: '/sms-terms', description: 'Text messaging terms and opt-out procedures' },
    ],
  };

  // Blog sections with enhanced categorization
  const blogSections = [];

  if (blogPosts.length > 0) {
    // General blog posts
    const generalPosts = blogPosts.filter(post => 
      !extractCityFromPost(post) && !post.slug.includes('template')
    );

    if (generalPosts.length > 0) {
      blogSections.push({
        title: 'General Roofing Blog',
        description: `Industry insights and general roofing information (${generalPosts.length} articles)`,
        priority: 'medium',
        links: generalPosts.map(post => ({
          name: post.seo_title || post.title,
          path: `/blog/${post.slug}`,
          description: post.seo_description || `Published ${new Date(post.published_date).toLocaleDateString()}`,
          lastmod: post.updated_at
        })),
      });
    }

    // Location-specific blog posts grouped by city
    const locationGroups = new Map<string, BlogPost[]>();
    
    blogPosts.forEach(post => {
      const city = extractCityFromPost(post);
      if (city && !post.slug.includes('template')) {
        if (!locationGroups.has(city)) {
          locationGroups.set(city, []);
        }
        locationGroups.get(city)!.push(post);
      }
    });

    // Add location-specific blog sections
    Array.from(locationGroups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([city, posts]) => {
        blogSections.push({
          title: `${city}, NC Roofing Blog`,
          description: `Local roofing content and services for ${city}, North Carolina (${posts.length} articles)`,
          priority: 'high',
          links: posts.map(post => ({
            name: post.seo_title || post.title,
            path: `/blog/${post.slug}`,
            description: post.seo_description || `Local roofing insights for ${city}, NC - Published ${new Date(post.published_date).toLocaleDateString()}`,
            lastmod: post.updated_at
          })),
        });
      });
  }

  // Combine all sections in logical order
  const allSections = [
    primaryNavSection,
    serviceSection,
    toolsSection,
    locationSection,
    informationSection,
    resourcesSection,
    specialPagesSection,
    ...blogSections,
    legalSection,
  ];

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-4">Complete Sitemap</h2>
            <p className="text-center text-muted-foreground mb-8">Loading comprehensive site navigation...</p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const totalPages = allSections.reduce((total, section) => total + section.links.length, 0);

  return (
    <Layout>
      <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* SEO-Optimized Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 text-brand-charcoal-gray">
              Carolina Trophy Roofs - Complete Sitemap
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Comprehensive site navigation covering {totalPages} pages across Eastern North Carolina
            </p>
            <div className="flex justify-center gap-6 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Last updated: {new Date().toLocaleDateString()}
              </div>
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                {blogPosts.length} blog articles
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {actualLocationPages.length} service locations
              </div>
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                SEO Optimized
              </div>
            </div>
          </div>

          {/* Priority Sections Grid */}
          <div className="grid gap-8">
            {allSections.map((section, index) => (
              <Card key={index} className={`${
                section.priority === 'high' ? 'border-2 border-brand-trophy-gold/30 bg-gradient-to-r from-yellow-50/50 to-white' :
                section.priority === 'medium' ? 'border-brand-carolina-blue/20 bg-blue-50/30' :
                'border-gray-200'
              }`}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold">{section.title}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={section.priority === 'high' ? 'default' : section.priority === 'medium' ? 'secondary' : 'outline'}
                        className={section.priority === 'high' ? 'bg-brand-trophy-gold text-white' : ''}
                      >
                        {section.priority} priority
                      </Badge>
                      <Badge variant="outline">
                        {section.links.length} {section.links.length === 1 ? 'page' : 'pages'}
                      </Badge>
                    </div>
                  </div>
                  {section.description && (
                    <p className="text-muted-foreground">{section.description}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {section.links.map((link, linkIndex) => (
                      <div key={linkIndex} className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                        {link.external ? (
                          <a 
                            href={link.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-carolina-blue hover:text-brand-trophy-gold transition-colors font-medium flex items-center gap-1 mb-2"
                          >
                            {link.name}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <Link 
                            to={link.path} 
                            className="text-brand-carolina-blue hover:text-brand-trophy-gold transition-colors font-medium flex items-center gap-1 mb-2"
                          >
                            {link.name}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        )}
                        {link.description && (
                          <p className="text-xs text-muted-foreground mb-2">{link.description}</p>
                        )}
                        <div className="text-xs text-gray-400">
                          {link.lastmod ? `Updated: ${new Date(link.lastmod).toLocaleDateString()}` : `Path: ${link.path}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Enhanced Company Information */}
          <Card className="mt-16 bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white">
            <CardContent className="p-8">
              <div className="grid lg:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-brand-trophy-gold">
                    <MapPin className="h-6 w-6" />
                    Headquarters
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-lg">Carolina Trophy Roofs</p>
                      <address className="not-italic text-gray-300">
                        104 S. Business Ct<br />
                        Rocky Mount, NC 27804
                      </address>
                    </div>
                    <div>
                      <p className="text-brand-trophy-gold font-semibold">Main Phone:</p>
                      <a href="tel:+12523022003" className="text-white hover:text-brand-trophy-gold transition-colors">
                        (252) 302-2003
                      </a>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-brand-trophy-gold">Additional Locations</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold">Greenville Office</p>
                      <address className="not-italic text-gray-300 text-sm">
                        1414 Forbes St<br />
                        Greenville, NC 27858
                      </address>
                      <p className="text-sm">
                        <a href="tel:+12523712266" className="text-white hover:text-brand-trophy-gold transition-colors">
                          (252) 371-2266
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold">Tarboro Service Area</p>
                      <address className="not-italic text-gray-300 text-sm">
                        Tarboro, NC 27886
                      </address>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-brand-trophy-gold">Contact Information</h3>
                  <div className="space-y-2">
                    <p>
                      <span className="font-semibold">Email:</span>{' '}
                      <a href="mailto:Claims@CarolinaTrophyRoofs.com" className="text-white hover:text-brand-trophy-gold transition-colors">
                        Claims@CarolinaTrophyRoofs.com
                      </a>
                    </p>
                    <p>
                      <span className="font-semibold">Website:</span>{' '}
                      <a href="https://www.carolinatrophyroofs.com" className="text-white hover:text-brand-trophy-gold transition-colors">
                        www.CarolinaTrophyRoofs.com
                      </a>
                    </p>
                    <div className="mt-4">
                      <p className="text-sm text-gray-300">
                        <strong>Service Area:</strong> Rocky Mount, Greenville, Wilson, Tarboro, Zebulon, and throughout Eastern North Carolina
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* XML Sitemap Notice */}
          <Card className="mt-8 border-brand-carolina-blue/20 bg-blue-50/30">
            <CardContent className="p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">XML Sitemap for Search Engines</h3>
              <p className="text-muted-foreground mb-4">
                This human-readable sitemap contains all {totalPages} pages on our website. 
                Search engines can also access our XML sitemap for automated indexing.
              </p>
              <div className="flex justify-center">
                <a 
                  href="/sitemap.xml" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-carolina-blue text-white px-4 py-2 rounded-lg hover:bg-brand-trophy-gold transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  View XML Sitemap
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SitemapPage;
